import React, { createContext, useContext, useEffect, useState } from 'react';
import { backendPost, backendGet } from '@/lib/backendClient';
import { mirrorSessionRemoval, mirrorSessionValue } from '@/mobile/session';
import { activateNativePushNotifications, deactivateNativePushNotifications } from '@/mobile/pushNotifications';

const AuthContext = createContext({});

const TOKEN_KEY = 'correfood_token';
const USER_KEY = 'correfood_user';

function loadStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveAuth(token, user) {
  const serializedUser = JSON.stringify(user);
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, serializedUser);
  void mirrorSessionValue(TOKEN_KEY, token);
  void mirrorSessionValue(USER_KEY, serializedUser);
}

function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  void mirrorSessionRemoval([TOKEN_KEY, USER_KEY]);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signIn(email, password) {
    try {
      await deactivateNativePushNotifications();
    } catch {
      // Melhor-esforco durante troca de conta.
    }

    // Troca de conta: limpa sessao anterior antes de autenticar novamente.
    clearAuth();
    setUser(null);

    const data = await backendPost(
      '/api/auth/login',
      { email, password },
      { skipAuth: true }
    );

    const fallbackUserData = {
      id: data.user_id,
      name: data.name,
      role: data.role,
      email
    };

    saveAuth(data.access_token, fallbackUserData);
    setUser(fallbackUserData);

    try {
      const me = await backendGet('/api/auth/me');
      const meUser = {
        id: me.user_id,
        name: me.name,
        role: me.role,
        email: me.email || email
      };
      saveAuth(data.access_token, meUser);
      setUser(meUser);
    } catch {
      // Mantem fallback da resposta de login se /me falhar.
    }

    return data;
  }

  async function signUp(email, password, name, role = 'user') {
    return await backendPost(
      '/api/auth/register',
      { name, email, password, role },
      { skipAuth: true }
    );
  }

  async function requestPasswordRecovery(email) {
    return await backendPost(
      '/api/auth/forgot-password',
      { email },
      { skipAuth: true }
    );
  }

  async function resendConfirmationEmail(email) {
    return await backendPost(
      '/api/auth/resend-confirmation',
      { email },
      { skipAuth: true }
    );
  }

  async function resetPasswordWithRecovery(accessToken, refreshToken, newPassword) {
    return await backendPost(
      '/api/auth/reset-password',
      { access_token: accessToken, refresh_token: refreshToken, new_password: newPassword },
      { skipAuth: true }
    );
  }

  async function signOut() {
    try {
      await deactivateNativePushNotifications();
    } catch {
      // Melhor-esforco para nao bloquear logout.
    }

    clearAuth();
    setUser(null);
  }

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        const storedUser = loadStoredUser();
        const token = localStorage.getItem(TOKEN_KEY);

        if (storedUser && isMounted) {
          setUser(storedUser);
        }

        if (token) {
          const me = await backendGet('/api/auth/me');
          const meUser = {
            id: me.user_id,
            name: me.name,
            role: me.role,
            email: me.email
          };
          saveAuth(token, meUser);
          if (isMounted) setUser(meUser);
        }
      } catch {
        clearAuth();
        if (isMounted) setUser(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearAuth();
      setUser(null);
      setLoading(false);
    };

    window.addEventListener('correfood:auth-expired', handleAuthExpired);
    return () => window.removeEventListener('correfood:auth-expired', handleAuthExpired);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    void activateNativePushNotifications();
  }, [user?.id]);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile: user,
        loading,
        signIn,
        signUp,
        requestPasswordRecovery,
        resendConfirmationEmail,
        resetPasswordWithRecovery,
        signOut,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
